import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

const KST = 'Asia/Seoul' as const;

/**
 * 서버가 내려주는 KST 벽시계 date-time 문자열(zone-less, 예: "2026-07-14T13:10:00")을
 * epoch ms로 변환한다. new Date()는 이 문자열을 기기 로컬 존으로 해석해 비KST 기기에서
 * 시각이 어긋나므로 항상 Asia/Seoul로 고정 파싱한다. 파싱 불가 값은 null(=지금 가능)로 본다.
 */
export const parseKstDateToMs = (value: string): number | null => {
  try {
    //* dayjs.tz는 파싱 불가 값에 isValid=false가 아니라 예외를 던지므로 함께 방어한다.
    const parsed = dayjs.tz(value, KST);

    return parsed.isValid() ? parsed.valueOf() : null;
  } catch {
    return null;
  }
};

export interface CountdownState {
  /** 지금 가능한지 */
  isAvailable: boolean;
  /** 가능해질 때까지 남은 초 */
  secondsUntilAvailable: number;
}

interface ResolveCountdownStateParams {
  /** 현재 시각(클라 시계) epoch ms */
  nowMs: number;
  /** 서버가 내려주는 다음 가능 시각 epoch ms. 지금 가능하면 null */
  nextAvailableAtMs: number | null;
}

/**
 * 다음 가능 시각(서버 권위)과 현재 시각으로 카운트다운 상태를 파생한다.
 * 쿠팡 적립·행운권 보너스 등 "다음 가능 시각까지 대기" UI가 공유한다.
 */
export const resolveCountdownState = ({
  nowMs,
  nextAvailableAtMs,
}: ResolveCountdownStateParams): CountdownState => {
  if (nextAvailableAtMs === null || nowMs >= nextAvailableAtMs) {
    return { isAvailable: true, secondsUntilAvailable: 0 };
  }

  return {
    isAvailable: false,
    secondsUntilAvailable: Math.ceil((nextAvailableAtMs - nowMs) / 1000),
  };
};

/** 남은 초를 HH:MM:SS로 표기한다. */
export const formatCountdown = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds]
    .map((unit) => String(unit).padStart(2, '0'))
    .join(':');
};
